import {APP_BOOTSTRAP_LISTENER, ErrorHandler, inject, NgModule, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {RouterModule} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr} from '@angular/common/http';
import {marked} from 'marked';

/*  Feature modules & services  */
import {AuthModule} from './services/auth/auth.module';
import {AuthService} from './services/auth/auth.service';
import {authInterceptor} from './interceptors/auth.interceptor';
// import {tracingInterceptor} from './interceptors/tracing.interceptor'; // FIN-0066: Disabled
import {authErrorInterceptor} from './interceptors/auth-error.interceptor';
import {httpDebugInterceptor} from './interceptors/http-debug.interceptor';
import {ConfigService} from './services/config.service';
import {CsrfInterceptor} from './services/csrf-interceptor.service';
import {TelemetryService} from './services/telemetry/telemetry.service';
// import {TracingModule} from './services/telemetry/tracing.module'; // FIN-0066: Temporarily disabled
import {WebSocketService} from './services/websocket.service';
import {
  ConnectionOrchestrator,
  ConnectionState,
  DisconnectReason
} from './services/connection/connection-manager.service';
import {ChatService} from './services/chat/chat.service';
import {ChatMessageProcessor} from './services/chat/chat-message-processor.service';
import {ChatCommunicationService} from './services/chat/chat-communication.service';
import {ChatProgressIntegrationService} from './services/websocket/chat-progress-integration.service';
import {TracingService} from './services/tracing.service';
import {MobilePerformanceService} from './services/mobile-performance.service';
import {ThemeService} from './services/theme.service';

/*  Components & directives  */
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LoginComponent} from './login/login.component';
import {MessageComponent} from './conversation/message/message.component';
import {PromptComponent} from './conversation/prompt/prompt.component';
import {ConversationShortcutComponent} from './conversation/conversation-shortcut/conversation-shortcut.component';
import {ChatMainComponent} from './chat/chat-main/chat-main.component';
import {MainSignInComponent} from './main/main-sign-in/main-sign-in.component';
import {ClickOutsideDirective} from './directives/click-outside.directive';
import {ViewportHeightDirective} from './directives/viewport-height.directive';
import {ErrorTrackingService} from './services/error-tracking.service';
import {PerformanceMetricsService} from './services/performance-metrics.service';
import { DemoThoughtTracerButtonComponent } from './development/demo-thought-tracer-button/demo-thought-tracer-button.component';
import {CookieDebugService} from './services/auth/cookie-debug.service';
import { WorkerDashboardComponent } from './components/worker-dashboard/worker-dashboard.component';
import { ChatHistorySidebarComponent } from './components/chat-history-sidebar/chat-history-sidebar.component';

/*  Markdown / DOMPurify configuration  */
marked.use({
  gfm: true,
  breaks: true,
  pedantic: false
});

export function performanceBootstrapFactory(performanceMetrics: PerformanceMetricsService) {
  return () => {
    // Track app bootstrap completion
    performanceMetrics.trackMilestone('application_bootstrapped');
  };
}

export function mobilePerformanceBootstrapFactory(mobilePerformance: MobilePerformanceService) {
  return () => {
    // Track app bootstrap completion
    mobilePerformance.trackAppBootstrapComplete();
  };
}

// FIN-0110: Theme service initialization - applies saved theme preference on app start
export function themeBootstrapFactory(themeService: ThemeService) {
  return () => {
    // ThemeService constructor auto-applies theme, this just ensures it's instantiated early
    console.log('[Bootstrap] Theme initialized:', themeService.preference, '-> effective:', themeService.effectiveTheme);
  };
}

/*  Application-startup initializer (replaces APP_INITIALIZER)  */
export async function initializeApp(): Promise<void> {
  const config = inject(ConfigService);
  try {
    console.log('[AppInit] Loading configuration.');
    await config.loadConfig();
    console.log('[AppInit] - Initialisation complete');
  } catch (err) {
    console.error('[AppInit] ? Initialisation failed:', err);
    return Promise.reject(err);
  }
}

/*  Bootstrap listeners  */
export function connectionBootstrapFactory(
  auth: AuthService,
  connection: ConnectionOrchestrator
) {
  return () => {
    if (auth.getAuthToken()) {
      console.log('[Bootstrap] User authenticated - establishing connection');
      connection.establishConnection(true);
    } else {
      console.log('[Bootstrap] User not authenticated - connection deferred');
    }
  };
}

export function telemetryBootstrapFactory(telemetry: TelemetryService) {
  return () => {
    console.log('[Bootstrap] Initializing telemetry with service name:', telemetry.getServiceName());
    telemetry.initialize().catch(err => {
      console.warn('[Bootstrap] Telemetry failed but app continues:', err);
    });
  };
}

// Shutdown handler for browser close/navigation
export function setupAppDestroyListener(
  webSocketService: WebSocketService,
  connectionManager: ConnectionOrchestrator
) {
  return () => {
    // Only register in browser environments
    if (typeof window !== 'undefined') {
      // Use a global flag to ensure we only register once
      const appWindow = window as any;
      if (!appWindow.__finderwise_destroy_handler_registered) {
        appWindow.__finderwise_destroy_handler_registered = true;

        // Handle actual window/tab closure with pagehide
        // This is more reliable than beforeunload for actual closures
        window.addEventListener('pagehide', (event) => {
          // If persisted is false, the page is being unloaded (closed, not refreshed)
          if (!event.persisted) {
            console.log('[Shutdown] Window/tab closing, cleaning up resources');

            // Clean up WebSocket service
            try {
              webSocketService.disconnect();
            } catch (error) {
              console.error('[Shutdown] Error disconnecting WebSocket:', error);
            }

            // Clean up connection manager
            try {
              if (connectionManager.getConnectionStatus().state !== ConnectionState.DISCONNECTED) {
                connectionManager.disconnect(DisconnectReason.NORMAL);
              }
            } catch (error) {
              console.error('[Shutdown] Error disconnecting from connection manager:', error);
            }
          } else {
            console.log('[Navigation] Page entering back/forward cache, not disconnecting');
          }
        });

        console.log('[Bootstrap] Registered page unload listener for cleanup');
      }
    }
  };
}


/*  NgModule definition  */
@NgModule({
  declarations: [
    AppComponent, // AppComponent is now in declarations
    LoginComponent,
    ClickOutsideDirective,
    PromptComponent,
    ConversationShortcutComponent,
    ChatMainComponent,
    MainSignInComponent,
    ViewportHeightDirective,
    WorkerDashboardComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    AppRoutingModule,
    NgOptimizedImage,
    FormsModule,
    AuthModule.forRoot(),
    // TracingModule, // FIN-0066: Temporarily disabled to test if telemetry is causing 8s HTTP/2 stall
    MessageComponent,
    DemoThoughtTracerButtonComponent,
    ChatHistorySidebarComponent,
  ],
  providers: [
    /* Core services */
    AuthService,
    ConfigService,
    TelemetryService,
    WebSocketService,
    ConnectionOrchestrator,
    ChatService,
    ChatMessageProcessor,
    ChatCommunicationService,
    ChatProgressIntegrationService,
    ErrorTrackingService,
    PerformanceMetricsService,
    TracingService,
    MobilePerformanceService,
    CookieDebugService,
    {
      provide: ErrorHandler,
      useClass: ErrorTrackingService
    },

    /* HTTP interceptors - order matters! */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CsrfInterceptor,
      multi: true
    },

    /* HTTP client helpers */
    provideHttpClient(withXhr(), 
      withInterceptors([
        httpDebugInterceptor,    // 1. Debug logging (dev/E2E only) - must be first to log everything
        // tracingInterceptor,   // FIN-0066: Disabled - calls OpenTelemetry API directly, bypassing config
        authInterceptor,         // 2. Add auth token to requests (was #3)
        authErrorInterceptor,    // 3. Handle 401 errors (after auth added, was #4)
      ]),
      withInterceptorsFromDi() // Legacy class-based interceptors (CsrfInterceptor)
    ),
    /* Application initializer */
    provideAppInitializer(initializeApp),
    /* Bootstrap listeners - All re-enabled */

    {
      provide: APP_BOOTSTRAP_LISTENER,
      useFactory: connectionBootstrapFactory,
      deps: [AuthService, ConnectionOrchestrator],
      multi: true
    },
    // FIN-0066: Telemetry bootstrap disabled (testing HTTP/2 stall fix)
    // {
    //   provide: APP_BOOTSTRAP_LISTENER,
    //   useFactory: telemetryBootstrapFactory,
    //   deps: [TelemetryService],
    //   multi: true
    // },
    {
      provide: APP_BOOTSTRAP_LISTENER,
      useFactory: setupAppDestroyListener,
      deps: [WebSocketService, ConnectionOrchestrator],
      multi: true
    },
    {
      provide: APP_BOOTSTRAP_LISTENER,
      useFactory: performanceBootstrapFactory,
      deps: [PerformanceMetricsService],
      multi: true
    },
    {
      provide: APP_BOOTSTRAP_LISTENER,
      useFactory: mobilePerformanceBootstrapFactory,
      deps: [MobilePerformanceService],
      multi: true
    },
    // FIN-0110: Theme service - initialize early to apply user's theme preference
    {
      provide: APP_BOOTSTRAP_LISTENER,
      useFactory: themeBootstrapFactory,
      deps: [ThemeService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
